import { useCallback, useMemo } from "react";
import { capitalize } from "lodash";
import { toast } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { useAppContext } from "~/contexts/appContext";
import { useGetCcToolsUserQuery } from "~/graphql/mainHooks";
import {
  useArenaAccessForUserQuery,
  useDeleteCanopyUserAndArenaAccessesMutation,
} from "~/graphql/supergraphHooks";
import { ArenaAccessRow } from "~/pages/Dashboard/components/WorkspaceTable";
import { useDateFormatterWithTimezone } from "~/pages/UserAndRoles/utils/dateFormatter";
import { formatShortFormDate } from "~/utils/datetime";
import { toGlobalId } from "~/utils/graphql-helpers";

type Args = { userId?: string };
export type RemoveRole = {
  roleId?: string | null;
  role?: string | null;
  fullName?: string | null;
  userId?: string | null;
};
export const useUserDetailsData = ({ userId }: Args) => {
  const globalUserId = useMemo(() => {
    return userId ? toGlobalId("UserObjectType", userId) : undefined;
  }, [userId]);

  const userResponse = useGetCcToolsUserQuery({
    variables: { userId: globalUserId! },
    context: withClientContext("staff"),
    fetchPolicy: "network-only",
    skip: !globalUserId,
  });

  const user = useMemo(() => {
    return {
      ...userResponse.data?.getUserForCcTools,
      groupNames: userResponse.data?.getUserForCcTools?.groups?.edges.map((g) => g.node.title),
      globalUserId,
    };
  }, [userResponse.data, globalUserId]);
  const rolesRowData = useMemo(() => {
    return (
      userResponse.data?.getUserForCcTools?.groups?.edges
        .map((g) => g.node)
        .map((g) => ({
          groupName: g.title || "",
          removeRole: {
            roleId: g.id,
            role: g.title,
            fullName: user.fullName,
            userId: user.globalUserId,
          },
        })) || []
    );
  }, [user, userResponse.data?.getUserForCcTools?.groups?.edges]);
  return {
    userResponse,
    user,
    rolesRowData,
  };
};

export const useArenaAccessForUser = (userId?: string) => {
  const { user } = useAppContext();
  const { formatDateWithTimezone } = useDateFormatterWithTimezone();
  const formatDateString = useCallback(
    (dateString: string | undefined, formatFunction: (d: string) => string) => {
      return typeof dateString === "string" ? formatFunction(dateString) : "--";
    },
    [],
  );

  const globalUserId = useMemo(() => {
    return userId ? toGlobalId("UserObjectType", userId) : undefined;
  }, [userId]);

  const arenaAccessForUserQueryResult = useArenaAccessForUserQuery({
    variables: {
      filters: {
        userId: globalUserId!,
        active: true,
      },
    },
    fetchPolicy: "network-only",
    skip: !globalUserId,
  });

  const rowData: ArenaAccessRow[] = useMemo(() => {
    return (
      arenaAccessForUserQueryResult.data?.arenaAccessForUser?.output
        .filter((item) => !item.isBulkAccess)
        .map((item) => ({
          id: item.id,
          arenaName: item.arenaName,
          arenaId: item.arenaId,
          arenaDomain: `${item.arenaDomain}.junipersquare.com`,
          roleGroupName: item.roleGroupName,
          active: item.active,
          accessType: capitalize(item.accessType),
          endDate: formatDateString(item.endDate ?? undefined, formatShortFormDate),
          lastAccess: formatDateWithTimezone(item.lastAccessTimestamp ?? undefined),
          login: {
            arenaId: item.arenaId,
            adminUserId: user.id,
            active: item.active,
          },
        })) || []
    );
  }, [arenaAccessForUserQueryResult.data, formatDateString, formatDateWithTimezone, user.id]);

  const { hasBulkArenaAccess, bulkAccessRoleGroupName } = useMemo(() => {
    const output = arenaAccessForUserQueryResult.data?.arenaAccessForUser?.output ?? [];
    const bulkAccess = output.find((item) => item.isBulkAccess);
    return {
      hasBulkArenaAccess: Boolean(bulkAccess),
      bulkAccessRoleGroupName: bulkAccess?.roleGroupName ?? "",
    };
  }, [arenaAccessForUserQueryResult.data]);

  return {
    rowData,
    arenaAccessForUserQueryResult,
    hasBulkArenaAccess,
    bulkAccessRoleGroupName,
  };
};

export const useRemoveUser = (userId?: string) => {
  const [deleteUser, { loading }] = useDeleteCanopyUserAndArenaAccessesMutation();

  const handleRemoveUser = useCallback(async () => {
    if (!userId) return;
    const response = await deleteUser({
      variables: {
        input: {
          userId: toGlobalId("UserObjectType", userId),
        },
      },
      refetchQueries: ["GetCCToolsUsers"],
    });

    const data = response.data?.deleteCanopyUserAndArenaAccesses;

    if (data?.__typename === "DeleteCanopyUserAndArenaAccessesResponse") {
      toast.success("User deleted successfully");
    } else if (data?.__typename === "MutationErrorResponse") {
      const errorMessage = data?.message ?? "Failed to delete user";
      toast.error(errorMessage);
    }
  }, [deleteUser, userId]);

  return { handleRemoveUser, loading };
};
